import React, { useEffect, useState } from 'react';
import { DrawerBody } from '@fiverr-private/overlay';
import pathfinder from '@fiverr-private/pathfinder';
import { Heading, Text } from '@fiverr-private/typography';
import { Stack } from '@fiverr-private/layout_components';
import { Image } from '@fiverr-private/media';
import { Button } from '@fiverr-private/button';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { AppI18n } from '../../i18n';
import final_image from '../../assets/final_image.svg';
import {
  AUCTION_SOURCE,
  BRIEF_CREATED_CLOUDINARY_GIF,
  FINDING_FREELANCERS_CLOUDINARY_GIF,
  MGMT_PAGE_TABS,
} from '../../constants';
import { useIsEligibleForProMigration } from '../ProMigrationStep/useIsEligibleForProMigration';
import { briefCreationClickAction } from '../../monitoring';
import BriefCreationCloseButton from '../BriefCreationDrawerComponent/BriefCreationCloseButton';
import { BriefsMobileBridge } from '../../events/BriefsMobileBridge';
import { buildUrlWithFromApp } from '../../utils';
import { PreMigrationState } from './PreMigrationState';

interface CurrentStep {
  imageSrc: string;
  textKey: React.ReactNode;
}

const LoadingState: React.FC<{ currentStep: CurrentStep }> = ({ currentStep }) => (
  <DrawerBody height="100%">
    <Stack direction="column" height="100%" width="100%" alignItems="center" justifyContent="center" gap="6">
      <Image src={currentStep.imageSrc} alt="brief created" width="100%" height="auto" />
      <Stack direction="column" alignItems="center" justifyContent="center">
        <Text size="b_lg" fontWeight="bold" textAlign="center">
          {currentStep.textKey}
        </Text>
      </Stack>
    </Stack>
  </DrawerBody>
);

interface FinalStateProps {
  finalImage: string;
}

const SOURCE = 'brief_completion_step';

const FinalState: React.FC<FinalStateProps> = ({ finalImage }) => {
  const { brief, isOpen, currentStep, contextId, source, variant, parentElement } = useBriefCreationContext();

  if (!isOpen) {
    return null;
  }

  const onViewBriefProgressClick = () => {
    briefCreationClickAction({
      step: currentStep,
      action: 'view_brief_progress',
      briefContextId: contextId,
      eventSource: source || '',
      variant,
      parentElement,
    });

    if (brief?.id) {
      const basePath = pathfinder(
        'dynamic_matching_management_page',
        { briefId: brief.id, tab: 'undefined' },
        { query: { source: SOURCE } }
      );
      window.location.href = buildUrlWithFromApp(basePath);
    }
  };

  const onSeeAllBriefsClick = () => {
    briefCreationClickAction({
      step: currentStep,
      action: 'see_all_briefs',
      briefContextId: contextId,
      eventSource: source || '',
      variant,
      parentElement,
    });

    BriefsMobileBridge.onOpenBriefLobby();

    const lobbyBasePath = pathfinder('briefs_lobby_page', {}, { query: { source: SOURCE } });
    window.location.href = buildUrlWithFromApp(lobbyBasePath);
  };

  return (
    <Stack padding="0" margin="0" height="100%" direction="column" width="100%" gap="0">
      <Stack padding="4" justifyContent="flexEnd">
        <BriefCreationCloseButton />
      </Stack>
      <DrawerBody height="100%">
        <Stack
          direction="column"
          height="100%"
          width="100%"
          alignItems="center"
          justifyContent="spaceBetween"
          paddingX="2"
        >
          <Stack direction="column" gap="8" alignItems="center" height="100%" justifyContent="center">
            <Image src={finalImage} alt="final brief" width="320px" height="180px" />
            <Stack direction="column" alignItems="center" justifyContent="center">
              <Heading as="h5" size="h_sm" fontWeight="bold" textAlign="center">
                <AppI18n k="finish_step.title" />
              </Heading>
              <Text textAlign="center">
                <AppI18n k="finish_step.description" />
              </Text>
            </Stack>
            <Stack direction="column" width="100%" gap="2">
              <Button onClick={onViewBriefProgressClick}>
                <AppI18n k="finish_step.view_brief_progress" />
              </Button>
              <Button onClick={onSeeAllBriefsClick} variant="ghost">
                <AppI18n k="finish_step.see_all_brief" />
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </DrawerBody>
    </Stack>
  );
};

export const FinishStep = () => {
  const { isBriefLoading, brief, isAllocatedAuctionInviteFlow } = useBriefCreationContext();
  const { isEligibleForProMigration } = useIsEligibleForProMigration();
  const [showLoadingState, setShowLoadingState] = useState(true);

  const [currentStep, setCurrentStep] = useState<CurrentStep>({
    imageSrc: BRIEF_CREATED_CLOUDINARY_GIF,
    textKey: <AppI18n key="first" k="finish_step.creating_brief" />,
  });

  useEffect(() => {
    const sourcingFreelancersTimer = setTimeout(() => {
      setCurrentStep({
        imageSrc: FINDING_FREELANCERS_CLOUDINARY_GIF,
        textKey: <AppI18n key="first" k="finish_step.sourcing_freelancers" />,
      });

      const loadingStateTimer = setTimeout(() => {
        setShowLoadingState(false);
      }, 3000);

      return () => {
        clearTimeout(loadingStateTimer);
      };
    }, 3000);

    return () => {
      clearTimeout(sourcingFreelancersTimer);
    };
  }, []);

  if (isBriefLoading || (isAllocatedAuctionInviteFlow && showLoadingState)) {
    return <LoadingState currentStep={currentStep} />;
  } else if (isAllocatedAuctionInviteFlow && !isBriefLoading && brief && !isEligibleForProMigration) {
    window.location.href = pathfinder(
      'dynamic_matching_management_page',
      {
        briefId: String(brief.id),
        tab: MGMT_PAGE_TABS.INVITE,
      },
      { query: { source: AUCTION_SOURCE } }
    );
    return <LoadingState currentStep={currentStep} />;
  }

  return isEligibleForProMigration ? <PreMigrationState /> : <FinalState finalImage={final_image} />;
};
